import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Visibility, VisibilityOff, Delete as DeleteIcon } from '@mui/icons-material';
import { auth } from '../firebase/config';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../firebase/config';
import { doc, setDoc, collection, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';

const SuperAdminDashboard = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    schoolName: '',
    adminEmail: '',
    adminPassword: '',
    adminName: '',
    phoneNumber: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [schools, setSchools] = useState([]);

  // Fonction pour récupérer la liste des écoles
  const fetchSchools = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'schools'));
      const schoolsData = [];
      
      for (const doc of querySnapshot.docs) {
        const schoolData = doc.data();
        schoolsData.push({
          id: doc.id,
          ...schoolData
        });
      }
      
      setSchools(schoolsData);
    } catch (error) {
      console.error('Erreur lors de la récupération des écoles:', error);
      setError('Erreur lors de la récupération des écoles');
    }
  };

  // Charger les écoles au montage du composant
  useEffect(() => {
    fetchSchools();
  }, []);

  const handleDelete = async (schoolId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette école ?')) {
      try {
        setLoading(true);
        // Supprimer l'école
        await deleteDoc(doc(db, 'schools', schoolId));
        // Rafraîchir la liste des écoles
        await fetchSchools();
        setSuccess('École supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression de l\'école');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      schoolName: '',
      adminEmail: '',
      adminPassword: '',
      adminName: '',
      phoneNumber: '',
    });
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // 1. Créer le compte utilisateur dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.adminEmail,
        formData.adminPassword
      );

      // 2. Créer le document utilisateur dans Firestore
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userDocRef, {
        email: formData.adminEmail,
        name: formData.adminName,
        role: 'admin',
        phoneNumber: formData.phoneNumber,
        createdAt: new Date().toISOString()
      });

      // 3. Créer le document école dans Firestore
      const schoolDocRef = doc(collection(db, 'schools'));
      await setDoc(schoolDocRef, {
        name: formData.schoolName,
        adminId: userCredential.user.uid,
        createdAt: new Date().toISOString()
      });

      // 4. Mettre à jour le document utilisateur avec l'ID de l'école
      await updateDoc(userDocRef, {
        schoolId: schoolDocRef.id
      });

      setSuccess('Administrateur et école créés avec succès !');
      handleClose();
      // Rafraîchir la liste des écoles
      await fetchSchools();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      let message = 'Une erreur est survenue lors de la création du compte';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          message = 'Cette adresse email est déjà utilisée';
          break;
        case 'auth/invalid-email':
          message = 'Adresse email invalide';
          break;
        case 'auth/operation-not-allowed':
          message = 'La création de compte est désactivée';
          break;
        case 'auth/weak-password':
          message = 'Le mot de passe est trop faible';
          break;
        default:
          message = error.message;
      }
      
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      
      <Container component="main" sx={{ mt: 4, mb: 4, flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography component="h1" variant="h4">
            Tableau de Bord Super Admin
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleClickOpen}
            disabled={loading}
          >
            Ajouter un Administrateur
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Établissement</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Administrateur</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schools.map((school) => (
                <TableRow key={school.id}>
                  <TableCell>{school.name}</TableCell>
                  <TableCell>{school.type}</TableCell>
                  <TableCell>{school.adminInfo?.name || ''}</TableCell>
                  <TableCell>{school.adminInfo?.email}</TableCell>
                  <TableCell>{school.adminInfo?.phoneNumber}</TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(school.id)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog 
          open={open} 
          onClose={handleClose}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>Ajouter un Administrateur</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Informations de l'Établissement
              </Typography>
              
              <TextField
                fullWidth
                required
                label="Nom de l'établissement"
                name="schoolName"
                value={formData.schoolName}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Type d'établissement</InputLabel>
                <Select
                  value={formData.schoolType}
                  name="schoolType"
                  onChange={handleChange}
                  label="Type d'établissement"
                  disabled={loading}
                >
                  <MenuItem value="Lycée">Lycée</MenuItem>
                  <MenuItem value="Collège">Collège</MenuItem>
                  <MenuItem value="École">École</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                Informations de l'Administrateur
              </Typography>

              <TextField
                fullWidth
                required
                label="Nom de l'administrateur"
                name="adminName"
                value={formData.adminName}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
              />

              <TextField
                fullWidth
                required
                type="email"
                label="Email de l'administrateur"
                name="adminEmail"
                value={formData.adminEmail}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
              />

              <TextField
                fullWidth
                required
                name="adminPassword"
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={formData.adminPassword}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                label="Numéro de téléphone"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Créer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SuperAdminDashboard;
